// Directorio de elementos (drivers) para búsqueda por nombre de persona.
// Requerimiento: "Buscar por nombre en los elementos/dispositivo" — el
// operador teclea el nombre del elemento (ej. "GALLEGOS") y encuentra su
// radio/unidad. Los monitors solo traen el id del conductor
// (attributes.conductor); el nombre vive en GET /v2/drivers, que aquí se
// carga UNA vez de forma lazy (al primer uso de una búsqueda) y se indexa
// id → nombre. Consumido por MapSearch.vue y MapSidebar.vue.
import ApiService from '@/util/api.service'
import { getDeviceConductor } from './conductor-links'

// id de driver → { name, norm } (norm = precalculado para matching barato
// por keystroke: lowercase sin acentos).
let byId = {}
let loadPromise = null
let loaded = false

// Lowercase + sin diacríticos: "González" → "gonzalez". Se aplica igual a
// la query y al haystack para que la búsqueda sea insensible a acentos.
export function normalizeText(value) {
  if (value === undefined || value === null) return ''
  return String(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

// Carga lazy del directorio. Idempotente: la primera llamada dispara el
// fetch, las siguientes reusan la promise. Si el fetch falla, se limpia
// para permitir retry en el siguiente intento de búsqueda.
export function ensureDriverDirectory() {
  if (loadPromise) return loadPromise
  loadPromise = ApiService({ url: '/v2/drivers', method: 'get' })
    .then((list) => {
      const next = {}
      const arr = Array.isArray(list) ? list : []
      for (let i = 0; i < arr.length; i++) {
        const d = arr[i]
        const id = d && (d.id || d._id)
        if (id != null && d.name) {
          // `photo` viaja junto al nombre porque es el MISMO fetch: el drawer
          // no tiene de dónde más sacar el retrato del elemento. Los devices
          // que llegan al mapa no traen el objeto `user` joined —ni los radios
          // ni las patrullas—, solo attributes.conductor con el id que se
          // resuelve aquí. Antes se descartaba y el drawer caía siempre al
          // avatar dummy.
          next[id] = {
            name: d.name,
            norm: normalizeText(d.name),
            photo: d.photo || '',
            adscripcion: d.adscripcion || '',
            categoria: d.categoria || '',
          }
        }
      }
      byId = next
      loaded = true
    })
    .catch((err) => {
      console.error('[driver-directory]', { err })
      loadPromise = null
    })
  return loadPromise
}

export function isDriverDirectoryLoaded() {
  return loaded
}

// Nombre del elemento para mostrar (subtítulo de resultados) a partir del
// raw de un device. '' si el device no tiene conductor o no resuelve.
export function getDriverNameForRaw(raw) {
  const conductorId = getDeviceConductor(raw)
  if (!conductorId) return ''
  const entry = byId[conductorId]
  return entry ? entry.name : ''
}

// Nombre normalizado (para matching) — precalculado en la carga para no
// re-normalizar por item en cada keystroke sobre listas de miles.
export function getDriverHaystackForRaw(raw) {
  const conductorId = getDeviceConductor(raw)
  if (!conductorId) return ''
  const entry = byId[conductorId]
  return entry ? entry.norm : ''
}

// Ficha del elemento asignado a un device: { name, photo, adscripcion,
// categoria } o null si el device no tiene conductor, el id no resuelve, o
// el directorio aún no se ha cargado.
//
// `photo` viene como ruta absoluta del servidor de archivos
// ('/app/public/images/photo-<n>.jpeg'); quien la consuma debe quedarse con
// el basename y anteponerle VITE_APP_IMAGES_PATH, igual que hace el drawer
// con la foto del usuario. Aquí se devuelve cruda para no atar este módulo
// —que es de datos— a una decisión de presentación.
export function getDriverInfoForRaw(raw) {
  const conductorId = getDeviceConductor(raw)
  if (!conductorId) return null
  return byId[conductorId] || null
}

// Ficha del elemento por su id, o null si no resuelve / no está cargado.
export function getDriverInfoById(id) {
  if (id === undefined || id === null) return null
  return byId[id] || null
}

// URL pública de una foto de elemento. La base de datos guarda la ruta del
// servidor de archivos ('/app/public/images/photo-<n>.jpeg') y la app la sirve
// bajo VITE_APP_IMAGES_PATH, así que se toma el basename y se reancla.
// Devuelve '' cuando no hay foto, para que quien llame decida el fallback.
export function driverPhotoUrl(photo) {
  if (!photo) return ''
  const base = import.meta.env.VITE_APP_IMAGES_PATH || '/api/v2/public/images/'
  return base + String(photo).split('/').pop()
}

// Solo para tests.
export function resetDriverDirectory() {
  byId = {}
  loadPromise = null
  loaded = false
}
